import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Artifacts } from '@/api/EmissionReportModel'
import { Card, Table, TableColumnHeader, TableRow } from '@/common'
import { EmissionTypeToReport } from './ProcessEmissionsSection'
import { ReportSectionCardHeader } from '../SectionCardHeader'
import { EmissionsIcon } from '@/common/components/icons'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import PfcProcessEmissionsTableRow from './PfcProcessEmissionTableRow'
import { EmissionCalculationMethods } from '@/shared/hooks/api/appConfig'
import SlopeMethodCalculatorPanel from '../calculators/aluminium/SlopeMethodCalculatorPanel'
import OvervoltageMethodCalculatorPanel from '../calculators/aluminium/OvervoltageMethodCalculatorPanel'

type EmissionEditState = {
  productionRouteAssetId: number
  emissionRecordId?: string
  method: string
}

export default function PfcProcessEmissionReporting({
  isViewMode,
  activityLevel,
  emissionsToReport,
  onEmissionSubmit,
  removeEmissionForAsset
}: {
  emissionsToReport: EmissionTypeToReport[]
  activityLevel: number
  isViewMode: boolean
  onEmissionSubmit: (
    payload: {
      emissionType: string
      method: string
      emission: number
      artifacts: Artifacts
      asset: ProductionRouteAsset
    },
    id?: string
  ) => void
  removeEmissionForAsset: (emissionRecordId: string) => void
  isComplete: boolean
}) {
  const { t } = useTranslation()
  const totalReportedEmissions = useMemo(() => {
    return emissionsToReport.reduce(
      (acc, emission) => acc + (emission.emissionRecord?.emission || 0),
      0
    )
  }, [emissionsToReport])

  const [editState, setEditState] = useState<EmissionEditState | null>(null)

  const editedProductionRouteAsset = useMemo(() => {
    if (editState === null) {
      return null
    }

    return (
      emissionsToReport.find(
        emission => emission.productionRouteAsset.id === editState.productionRouteAssetId
      ) || null
    )
  }, [editState, emissionsToReport])

  const onSubmit = useCallback(
    (emission: number, artifacts: Artifacts) => {
      if (editedProductionRouteAsset === null || !editState?.method) {
        return
      }

      const existingRecord = editedProductionRouteAsset.emissionRecord

      onEmissionSubmit(
        {
          asset: editedProductionRouteAsset.productionRouteAsset,
          emissionType: 'PFC',
          method: editState.method,
          emission,
          artifacts
        },
        existingRecord?.id
      )
    },
    [editedProductionRouteAsset]
  )

  const onEmissionRemove = useCallback(
    (emissionRecordId: string) => {
      removeEmissionForAsset(emissionRecordId)
      setEditState(null)
    },
    [removeEmissionForAsset]
  )

  const isAddingPFCEmissionsUsingOvervoltage =
    editState?.method === EmissionCalculationMethods.PfcOvervoltage
  const isAddingPFCEmissionsUsingSlope = editState?.method === EmissionCalculationMethods.PfcSlope

  const percentageOfAssetsWithEmissions = useMemo(() => {
    return (
      (emissionsToReport.filter(emission => emission.emissionRecord?.emission).length /
        emissionsToReport.length) *
      100
    )
  }, [emissionsToReport])

  return (
    <>
      <Card>
        <ReportSectionCardHeader
          icon={<EmissionsIcon className="size-4" />}
          title={t('emissionTypes.PFC')}
          emissions={totalReportedEmissions}
          completionPercentage={percentageOfAssetsWithEmissions}
        />
        <Table dense borderless>
          <thead>
            <TableRow>
              <TableColumnHeader className="w-64" />
              <TableColumnHeader className="w-36">
                {t('report:processEmissions.table.headers.customId')}
              </TableColumnHeader>
              <TableColumnHeader className="w-48">
                {t('report:processEmissions.table.headers.calculationMethod')}
              </TableColumnHeader>
              <TableColumnHeader className="w-48 text-right">
                {t('report:processEmissions.table.headers.emission')}
              </TableColumnHeader>
              <TableColumnHeader className="w-44" />
            </TableRow>
          </thead>
          {emissionsToReport.map(emission => (
            <PfcProcessEmissionsTableRow
              key={emission.productionRouteAsset.id}
              assetWithMetadata={emission}
              setIsAddingEmission={setEditState}
              isViewMode={isViewMode}
              onEmissionRemove={onEmissionRemove}
            />
          ))}
        </Table>
      </Card>
      {isAddingPFCEmissionsUsingOvervoltage && (
        <OvervoltageMethodCalculatorPanel
          aluminiumQuantity={activityLevel}
          initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
          addEmission={onSubmit}
          assetId={editedProductionRouteAsset?.productionRouteAsset.assetDefinitionId || ''}
          isOpen={isAddingPFCEmissionsUsingOvervoltage}
          close={() => setEditState(null)}
        />
      )}
      {isAddingPFCEmissionsUsingSlope && (
        <SlopeMethodCalculatorPanel
          aluminiumQuantity={activityLevel}
          addEmission={onSubmit}
          assetId={editedProductionRouteAsset?.productionRouteAsset.assetDefinitionId || ''}
          initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
          isOpen={isAddingPFCEmissionsUsingSlope}
          close={() => setEditState(null)}
        />
      )}
    </>
  )
}
